import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, CheckCircle, QrCode, MapPin, Gift, Users, TrendingUp, Target, Eye, Award, Zap, BarChart3, DollarSign, Rocket, Phone, Mail, MapPinned, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../config';
import AuthModal from '../components/AuthModal';

const PitchDeck = () => {
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showNDA, setShowNDA] = useState(false);
  const [ndaAgreed, setNdaAgreed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkAuthAndNDA = async () => {
      // If not logged in, don't show anything yet
      if (!user) {
        setLoading(false);
        return;
      }

      // User is logged in, check NDA status from backend
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/pitch-deck/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.nda_agreed) {
            setNdaAgreed(true);
            setShowNDA(false);
            // Track view
            trackView();
          } else {
            setShowNDA(true);
          }
        } else {
          // API failure must DENY, not grant: default to requiring the NDA.
          setShowNDA(true);
        }
      } catch (err) {
        console.error('Error checking NDA status:', err);
      }
      
      setLoading(false);
    };

    checkAuthAndNDA();
  }, [user]);

  const trackView = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/pitch-deck/track-view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error('Error tracking view:', err);
    }
  };

  const handleNDASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/pitch-deck/agree-nda`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ agreed: true })
      });

      if (response.ok) {
        setNdaAgreed(true);
        setShowNDA(false);
        // Track initial view
        trackView();
      } else {
        setError('Failed to record NDA agreement. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting NDA:', err);
      setError('An error occurred. Please try again.');
    }
    
    setLoading(false);
  };

  // Auth Gate - Require login first
  if (!user && !loading) {
    return (
      <>
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)} 
          initialMode="login" 
        />
        <div style={{ backgroundColor: '#FFF6EA', minHeight: '100vh' }} className="flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 md:p-12 text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-purple-100 mb-6">
              <Lock className="w-10 h-10 text-purple-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Fraunces, serif', color: '#111827' }}>
              Investor Pitch Deck
            </h1>
            <p className="text-lg mb-8" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Please sign in to your account to access our confidential investor pitch deck.
            </p>
            <Button
              onClick={() => setShowAuthModal(true)}
              className="w-full rounded-full py-6 text-lg font-bold"
              style={{ backgroundColor: '#6F5BEB', color: '#FFFFFF' }}
            >
              Sign In to Continue
            </Button>
            <p className="mt-6 text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#9CA3AF' }}>
              Don't have an account?{' '}
              <button
                onClick={() => setShowAuthModal(true)}
                className="font-semibold hover:underline"
                style={{ color: '#6F5BEB' }}
              >
                Sign up here
              </button>
            </p>
          </motion.div>
        </div>
      </>
    );
  }

  // NDA Modal - Show after user is authenticated
  if (showNDA && user) {
    return (
      <div style={{ backgroundColor: '#FFF6EA', minHeight: '100vh' }} className="flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 md:p-12"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
              <CheckCircle className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Fraunces, serif', color: '#111827' }}>
              Confidentiality Agreement
            </h1>
            <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Hi {user.first_name}, please review and accept our NDA to access the investor pitch deck.
            </p>
          </div>

          <form onSubmit={handleNDASubmit} className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-6 text-left">
              <h3 className="font-bold mb-3" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                Non-Disclosure Agreement
              </h3>
              <p className="text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280', lineHeight: '1.6' }}>
                By accessing this pitch deck, you agree to keep all information confidential and acknowledge that:
              </p>
              <ul className="text-sm space-y-2" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                <li>• This information is intended for potential investors only</li>
                <li>• You will not share, reproduce, or distribute this content</li>
                <li>• All proprietary information remains the property of Tappd</li>
                <li>• You will use this information solely for investment evaluation purposes</li>
              </ul>
            </div>

            {error && (
              <p className="text-red-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full py-6 text-lg font-bold"
              style={{ backgroundColor: '#6F5BEB', color: '#FFFFFF' }}
            >
              {loading ? 'Processing...' : 'I Agree - View Pitch Deck'}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ backgroundColor: '#FFF6EA', minHeight: '100vh' }} className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Main Pitch Deck
  return (
    <div style={{ backgroundColor: '#FFF6EA' }}>
      {/* SLIDE 1: HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{
        background: 'linear-gradient(135deg, #6F5BEB 0%, #FF7A1A 100%)'
      }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center text-white">
          <img 
            src="/tappd-logo.png" 
            alt="Tappd" 
            className="mx-auto mb-8"
            style={{ height: '60px', width: 'auto', filter: 'brightness(0) invert(1)' }}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-6 py-2 rounded-full mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>🍺 FOR BARS & RESTAURANTS</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', lineHeight: '1.1' }}>
              Get Your Business in Front of <span style={{ color: '#FFD700' }}>Thousands of Locals</span> — Every Day.
            </h1>

            <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto" style={{ fontFamily: 'Inter, sans-serif', opacity: 0.9 }}>
              Tappd turns everyday bar coasters into powerful local ads that reach real people in your community. No screens. No algorithms. Just results.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#problem" className="inline-flex items-center justify-center px-8 py-4 rounded-full text-lg font-bold" style={{ backgroundColor: '#FFFFFF', color: '#6F5BEB' }}>
                See How It Works →
              </a>
              <a href="#investment" className="inline-flex items-center justify-center px-8 py-4 rounded-full text-lg font-bold border-2 border-white" style={{ color: '#FFFFFF' }}>
                Investment Opportunity
              </a>
            </div>
          </motion.div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white opacity-50" />
          </div>
        </div>
      </section>

      {/* SLIDE 2: THE PROBLEM */}
      <section id="problem" className="min-h-screen py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#FEE2E2' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#DC2626' }}>⚠️ THE PROBLEM</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              A Two-Sided Problem
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Local businesses can't reach their actual customers, and bars can't afford basic supplies.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg border-l-4" style={{ borderColor: '#FF7A1A' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                For Local Businesses
              </h3>
              <p className="text-lg mb-6" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                They spend thousands on Facebook and Google ads with zero guarantee of reaching actual local customers who will walk through their doors.
              </p>
              <div className="bg-orange-50 rounded-2xl p-6 text-center">
                <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#FF7A1A' }}>$0 ROI</div>
                <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>on social ads for hyperlocal reach</div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg border-l-4" style={{ borderColor: '#6F5BEB' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                For Bars & Restaurants
              </h3>
              <p className="text-lg mb-6" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                They rely on inconsistent "free" coasters from beverage partners—when available. When not, they pay for napkins themselves or serve drinks without protection.
              </p>
              <div className="bg-purple-50 rounded-2xl p-6 text-center">
                <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#6F5BEB' }}>Unpredictable</div>
                <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>supply of basic bar essentials</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#FF7A1A' }}>1000s</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Locals at bars every week</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#6F5BEB' }}>0%</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Digital ads reaching them in that moment</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#10B981' }}>100%</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Bars need consistent coaster supply</div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <p className="text-lg leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', color: '#374151' }}>
              <strong>The Gap:</strong> Thousands of potential local customers are physically present in venues, relaxed and receptive—but completely unreachable through traditional digital advertising. Meanwhile, venues struggle with basic supplies. <strong style={{ color: '#FF7A1A' }}>Tappd solves both problems.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* SLIDE 3: THE SOLUTION */}
      <section className="min-h-screen py-20 px-6" style={{ backgroundColor: '#FFF6EA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#FEF3C7' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#D97706' }}>✨ THE SOLUTION</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              We Turn Coasters Into Real-World Ad Networks
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Physical presence meets digital tracking. Every coaster is a discovery opportunity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: '#FFF6EA' }}>
                <span className="text-4xl">🍺</span>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Free to Venues
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Bars and restaurants get premium branded coasters at zero cost. They get quality materials, we get strategic placement.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: '#FFF6EA' }}>
                <span className="text-4xl">📱</span>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                QR Simplicity
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                One scan with a phone camera—no app required. Instant access to curated local businesses and exclusive offers.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: '#FFF6EA' }}>
                <span className="text-4xl">🎯</span>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Hyperlocal Targeting
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Smart algorithm shows only relevant businesses based on venue location. Your customers see businesses they can actually visit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SLIDE 4: HOW IT WORKS */}
      <section className="min-h-screen py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#DBEAFE' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#1D4ED8' }}>📍 SIMPLE PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              How It Works
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Three simple steps to discover local businesses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="relative mb-6">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4" style={{ backgroundColor: '#6F5BEB' }}>
                  <QrCode className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#FF7A1A' }}>
                  01
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Grab a Coaster
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Pick up your drink at a participating taproom. Each coaster features a unique QR code.
              </p>
            </div>

            <div className="text-center">
              <div className="relative mb-6">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4" style={{ backgroundColor: '#6F5BEB' }}>
                  <Eye className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#FF7A1A' }}>
                  02
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Scan & Discover
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Open your camera and scan. Instantly see curated local businesses with exclusive offers.
              </p>
            </div>

            <div className="text-center">
              <div className="relative mb-6">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4" style={{ backgroundColor: '#6F5BEB' }}>
                  <Gift className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#FF7A1A' }}>
                  03
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Claim Your Deal
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Tap to claim the offer and visit. Every interaction is tracked for real ROI insights.
              </p>
            </div>
          </div>

          <div className="bg-green-50 rounded-3xl p-8 shadow-lg text-center border-2" style={{ borderColor: '#10B981' }}>
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <p className="text-xl font-semibold" style={{ fontFamily: 'Inter, sans-serif', color: '#059669' }}>
              No App Required • Instant Access • Real-Time Tracking
            </p>
          </div>
        </div>
      </section>

      {/* SLIDE 4.5: REAL COASTER EXAMPLES */}
      <section className="min-h-screen py-20 px-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF7A1A 0%, #6F5BEB 100%)' }}>
        <div className="max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF' }}>🎨 REAL EXAMPLES</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#FFFFFF' }}>
              Real Tappd Coasters in Action
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', opacity: 0.9 }}>
              Themed designs that capture attention and drive engagement
            </p>
          </motion.div>

          {/* Coaster Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              {
                url: 'https://customer-assets.emergentagent.com/job_tappd-signup/artifacts/gfin7jc3_Tappd-XFILES-MQ-v2.png',
                title: 'X-Files Mystery Theme',
                delay: 0
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_tappd-signup/artifacts/a3403rq7_Tappd-SEVEN-MQ-v2.png',
                title: 'Seven Detective Theme',
                delay: 0.1
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_tappd-signup/artifacts/w0xgasot_Tappd-MATRIX-MQ-v1.png',
                title: 'Matrix Tech Theme',
                delay: 0.2
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_tappd-signup/artifacts/eifks6g6_Tappd-JE-MQ-v2.png',
                title: 'Classic Film Theme',
                delay: 0.3
              }
            ].map((coaster, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8, rotateY: 180 }}
                whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.6, 
                  delay: coaster.delay,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  scale: 1.05,
                  rotate: 5,
                  transition: { duration: 0.2 }
                }}
              >
                <div className="bg-white rounded-2xl p-4 shadow-2xl">
                  <img
                    src={coaster.url}
                    alt={coaster.title}
                    className="w-full h-auto rounded-lg mb-3"
                    style={{
                      aspectRatio: '1/1',
                      objectFit: 'cover'
                    }}
                  />
                  <p className="text-sm font-semibold text-center" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>
                    {coaster.title}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Key Features */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20"
          >
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#FFFFFF' }}>
                  4+
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', opacity: 0.9 }}>
                  Unique Themed Designs
                </p>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#FFFFFF' }}>
                  Premium
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', opacity: 0.9 }}>
                  Quality Materials
                </p>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#FFFFFF' }}>
                  100%
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', opacity: 0.9 }}>
                  Attention Grabbing
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SLIDE 5: PRICING */}
      <section className="min-h-screen py-20 px-6" style={{ backgroundColor: '#FFF6EA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#D1FAE5' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#059669' }}>💰 PRICING</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              Fair, Transparent Pricing
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Choose the tier that matches your marketing budget and visibility goals
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="mb-6">
                <div className="text-sm font-bold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Basic</div>
                <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>$113</div>
                <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>per month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>1× visibility weight</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Standard rotation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Analytics dashboard</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Monthly reports</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="mb-6">
                <div className="text-sm font-bold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Medium</div>
                <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>$188</div>
                <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>per month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>3× visibility weight</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Priority rotation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Advanced analytics</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Weekly reports</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="mb-6">
                <div className="text-sm font-bold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>High</div>
                <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>$300</div>
                <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>per month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>5× visibility weight</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Premium rotation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Real-time analytics</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#374151', fontSize: '14px' }}>Custom reporting</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-orange-500 rounded-3xl p-8 shadow-2xl border-2 relative" style={{ borderColor: '#FFD700' }}>
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#FFD700', color: '#111827' }}>
                ⭐ Premium
              </div>
              <div className="mb-6">
                <div className="text-sm font-bold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF' }}>Max</div>
                <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#FFFFFF' }}>$1200</div>
                <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', opacity: 0.9 }}>per month</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', fontSize: '14px' }}>10× visibility weight</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', fontSize: '14px' }}>Maximum exposure</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', fontSize: '14px' }}>Dedicated support</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span style={{ fontFamily: 'Inter, sans-serif', color: '#FFFFFF', fontSize: '14px' }}>Daily insights</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border-l-4" style={{ borderColor: '#10B981' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              ⚖️ Fair Play Algorithm
            </h3>
            <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              When a business receives more than 10 views in a session, its weighting temporarily drops by 50%. This ensures all advertisers get fair, consistent exposure. No monopolies. Pure transparency.
            </p>
          </div>
        </div>
      </section>

      {/* SLIDE 6: WHY IT WORKS */}
      <section className="min-h-screen py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#FEF3C7' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#D97706' }}>💡 KEY ADVANTAGES</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              Why It Works
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Four fundamental reasons this model succeeds where digital ads fail
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="text-4xl mb-4">🎪</div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Context = Curiosity
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                People naturally scan coasters. It's a physical novelty that breaks the monotony of scrolling. Location + context = engagement.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Zero Ad Fatigue
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Physical presence in trusted venues replaces noisy, ignored digital feeds. Customers discover businesses when relaxed and receptive.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Real, Transparent Data
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Every scan, view, and claim is tracked. No black box algorithms. No fake metrics. Just honest, actionable insights.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Win-Win-Win Model
              </h3>
              <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Bars get free premium coasters. Businesses get hyper-targeted exposure. Customers discover what they actually want.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SLIDE 7: ROI EXAMPLE */}
      <section className="min-h-screen py-20 px-6" style={{ backgroundColor: '#FFF6EA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#D1FAE5' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#059669' }}>📈 PROVEN ROI</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              Real Numbers, Real Results
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Example from a single hub: Ott's Tavern — 1,000 scans per month
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="inline-block px-4 py-1 rounded-full text-xs font-bold mb-4" style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
                HIGH TIER
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Sunflower Café
              </h3>
              <div className="text-lg mb-4" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                250 views • 25 claims
              </div>
              <div className="space-y-2 text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                <div>Monthly Cost: <strong style={{ color: '#111827' }}>$300</strong></div>
                <div>Cost per View: <strong style={{ color: '#111827' }}>$1.20</strong></div>
                <div>Cost per Claim: <strong style={{ color: '#111827' }}>$12.00</strong></div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="inline-block px-4 py-1 rounded-full text-xs font-bold mb-4" style={{ backgroundColor: '#E0E7FF', color: '#4F46E5' }}>
                MEDIUM
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Medford Yoga
              </h3>
              <div className="text-lg mb-4" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                150 views • 15 claims
              </div>
              <div className="space-y-2 text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                <div>Monthly Cost: <strong style={{ color: '#111827' }}>$188</strong></div>
                <div>Cost per View: <strong style={{ color: '#111827' }}>$1.25</strong></div>
                <div>Cost per Claim: <strong style={{ color: '#111827' }}>$12.53</strong></div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="inline-block px-4 py-1 rounded-full text-xs font-bold mb-4" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                BASIC
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Blossom Spa
              </h3>
              <div className="text-lg mb-4" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                100 views • 10 claims
              </div>
              <div className="space-y-2 text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                <div>Monthly Cost: <strong style={{ color: '#111827' }}>$113</strong></div>
                <div>Cost per View: <strong style={{ color: '#111827' }}>$1.13</strong></div>
                <div>Cost per Claim: <strong style={{ color: '#111827' }}>$11.30</strong></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border-l-4" style={{ borderColor: '#3B82F6' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              📊 Compare to Facebook Ads
            </h3>
            <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              $5 per click with zero guarantee of local relevance or foot traffic. Tappd delivers <strong style={{ color: '#FF7A1A' }}>$1.25 per view</strong> and <strong style={{ color: '#6F5BEB' }}>$12.53 per claim</strong> from real, local, engaged customers. That's <strong style={{ color: '#10B981' }}>4× better ROI</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* SLIDE 8: REVENUE MODEL */}
      <section className="min-h-screen py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#D1FAE5' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#059669' }}>💵 REVENUE MODEL</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              Scalable, Predictable Revenue
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Phased revenue growth: Starting with 10 businesses, scaling to 100+ advertisers
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Phase 1: Beta
              </h3>
              <div className="text-lg mb-6" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Month 2-3 • 1 hub • 10 advertisers
              </div>
              <div className="space-y-2 mb-6" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                <div>4 High @ $300/mo: <strong style={{ color: '#111827' }}>$1,200</strong></div>
                <div>4 Medium @ $188/mo: <strong style={{ color: '#111827' }}>$752</strong></div>
                <div>2 Basic @ $113/mo: <strong style={{ color: '#111827' }}>$226</strong></div>
              </div>
              <div className="pt-4 border-t-2">
                <div className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#10B981' }}>
                  Beta MRR: $2,178
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg border-2" style={{ borderColor: '#6F5BEB' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                Phase 2: Growth
              </h3>
              <div className="text-lg mb-6" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                Month 3-6 • 3 hubs • 100 advertisers
              </div>
              <div className="space-y-2 mb-6" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280', fontSize: '15px' }}>
                <div className="flex items-center justify-between">
                  <span>65 Basic @ $113/mo:</span>
                  <strong style={{ color: '#111827' }}>$7,345</strong>
                </div>
                <div className="flex items-center justify-between">
                  <span>25 Medium @ $188/mo:</span>
                  <strong style={{ color: '#111827' }}>$4,700</strong>
                </div>
                <div className="flex items-center justify-between">
                  <span>12 High @ $300/mo:</span>
                  <strong style={{ color: '#111827' }}>$3,600</strong>
                </div>
                <div className="flex items-center justify-between">
                  <span>5 Max @ $1,200/mo:</span>
                  <strong style={{ color: '#FF7A1A' }}>$6,000</strong>
                </div>
              </div>
              <div className="pt-4 border-t-2" style={{ borderColor: '#E5E7EB' }}>
                <div className="text-sm mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                  107 total subscribers
                </div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#10B981' }}>
                  Growth MRR: $21,645
                </div>
              </div>
              <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: '#F3F4F6' }}>
                <div className="text-xs font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>DISTRIBUTION RATIONALE:</div>
                <ul className="text-xs space-y-1" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                  <li>• Most businesses start with Basic (test the waters)</li>
                  <li>• Medium is the sweet spot after seeing ROI</li>
                  <li>• High tier for established businesses</li>
                  <li>• Max for premium venues (Homeland Vineyard, etc.)</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl p-8 text-center shadow-xl border-2" style={{ borderColor: '#6F5BEB' }}>
              <div className="text-6xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#6F5BEB' }}>$21,645</div>
              <div className="text-lg font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>Monthly Recurring Revenue</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>At 107 subscribers across 3 hubs</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 text-center shadow-xl border-2" style={{ borderColor: '#10B981' }}>
              <div className="text-6xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#10B981' }}>$259,740</div>
              <div className="text-lg font-semibold mb-2" style={{ fontFamily: 'Inter, sans-serif', color: '#111827' }}>Annual Recurring Revenue</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Pure subscription revenue model</div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border-l-4" style={{ borderColor: '#6F5BEB' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              🚀 Scalability Advantage
            </h3>
            <p className="text-lg" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              Low overhead = exponential growth. Each additional hub adds new ad inventory at near-zero marginal cost. Coaster printing scales linearly, but revenue scales exponentially as network effects kick in.
            </p>
          </div>
        </div>
      </section>

      {/* SLIDE 9: TRACTION */}
      <section className="min-h-screen py-20 px-6" style={{ backgroundColor: '#FFF6EA' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#D1FAE5' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#059669' }}>✅ CURRENT PROGRESS</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              Traction & Momentum
            </h2>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              What we've accomplished and what's next
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                ✓ Completed
              </h3>
              <div className="space-y-4" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>Algorithm & pricing model designed</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>Brand identity & logo developed</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>Local venue partnerships forming</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <span>MVP launching in Medford, NJ</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
                📅 6-Month Roadmap
              </h3>
              <div className="space-y-4" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                <div className="flex items-start gap-3">
                  <div className="text-sm font-bold flex-shrink-0" style={{ color: '#6F5BEB', minWidth: '60px' }}>Mo 1-2:</div>
                  <span>Build dashboard v1.1.0</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-sm font-bold flex-shrink-0" style={{ color: '#6F5BEB', minWidth: '60px' }}>Mo 2-3:</div>
                  <span>Beta launch: 10 advertisers, 1 hub</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-sm font-bold flex-shrink-0" style={{ color: '#6F5BEB', minWidth: '60px' }}>Mo 3-4:</div>
                  <span>Expand to 100 advertisers, 3 hubs</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-sm font-bold flex-shrink-0" style={{ color: '#6F5BEB', minWidth: '60px' }}>Mo 5-6:</div>
                  <span>Scale to 150+ advertisers, 5-8 hubs</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SLIDE 10: INVESTMENT ASK */}
      <section id="investment" className="min-h-screen py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#FEF3C7' }}>
              <span className="text-sm font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#D97706' }}>🎯 INVESTMENT OPPORTUNITY</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              Join the Hyperlocal Revolution
            </h2>
          </div>

          <div className="bg-gradient-to-br from-purple-600 to-orange-500 rounded-3xl p-12 text-white text-center mb-12 shadow-2xl">
            <h3 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Friends & Family Seed Round
            </h3>
            <div className="text-7xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              $50,000
            </div>
            <p className="text-xl" style={{ fontFamily: 'Inter, sans-serif', opacity: 0.9 }}>
              To launch, prove, and scale the platform to profitability
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#6F5BEB' }}>$20K</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Platform Development</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#FF7A1A' }}>$15K</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Coaster Production</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#10B981' }}>$10K</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Marketing & Sales</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <div className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#3B82F6' }}>$5K</div>
              <div className="text-sm" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>Legal & Operations</div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-lg border-l-4" style={{ borderColor: '#10B981' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              🎯 12-Month Projection
            </h3>
            <div className="text-lg leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
              3-8 active hubs across South Jersey<br />
              100-150 advertisers generating predictable MRR<br />
              $260K-$350K ARR potential by end of Year 1<br />
              Proof of concept ready for Series A
            </div>
          </div>
        </div>
      </section>

      {/* SLIDE 11: VISION & CONTACT */}
      <section className="min-h-screen flex items-center justify-center py-20 px-6" style={{
        background: 'linear-gradient(135deg, #6F5BEB 0%, #FF7A1A 100%)'
      }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="h-1 w-24 mx-auto mb-8 rounded-full" style={{ background: 'linear-gradient(90deg, #FFD700, #FFA500)' }}></div>

          <h2 className="text-4xl md:text-6xl font-bold mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            The First Physical Ad Network for Local Communities
          </h2>

          <p className="text-xl md:text-2xl mb-16" style={{ fontFamily: 'Inter, sans-serif', opacity: 0.9 }}>
            Where every coaster, table tent, and scan connects people to the businesses that make their neighborhoods unique and vibrant.
          </p>

          <div className="bg-white rounded-3xl p-8 md:p-12 text-left shadow-2xl">
            <h3 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#111827' }}>
              Let's Build This Together
            </h3>

            <div className="space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="flex items-center gap-4">
                <Users className="w-6 h-6 text-purple-600 flex-shrink-0" />
                <div>
                  <div className="font-bold text-lg" style={{ color: '#111827' }}>Rauf Tur</div>
                  <div style={{ color: '#6B7280' }}>Founder, Tappd</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Mail className="w-6 h-6 text-purple-600 flex-shrink-0" />
                <a href="mailto:hello@tappd.app" className="text-lg" style={{ color: '#6F5BEB' }}>
                  hello@tappd.app
                </a>
              </div>

              <div className="flex items-center gap-4">
                <Phone className="w-6 h-6 text-purple-600 flex-shrink-0" />
                <a href="tel:6098927152" className="text-lg" style={{ color: '#6F5BEB' }}>
                  (609) 892-7152
                </a>
              </div>

              <div className="flex items-center gap-4">
                <MapPinned className="w-6 h-6 text-purple-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold" style={{ color: '#111827' }}>iOLab Digital Marketing</div>
                  <div style={{ color: '#6B7280' }}>Medford Lakes, New Jersey</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PitchDeck;
